import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { userInfoSync } from 'hub-http/userInfo';
import { orderedSteps } from '../../constants/ordered-form-steps';
import { useAllowedAssets } from '../../hooks/use-allowed-assets';
import { useCreateSyncTask } from '../../hooks/mutations/use-create-sync-task';
import { useCopyAssetTaskStatus } from '../../hooks/queries/use-copy-asset-task-status';
import { usePortableKeys } from '../../hooks/queries/use-portable-keys';
import { useTargetHubs } from '../../hooks/queries/use-target-hubs';
import { trackInteraction } from '../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
const AssetCopierContext = /*#__PURE__*/createContext(undefined);
export function AssetCopierProvider(props) {
  const {
    children,
    assets,
    portableType,
    onCopyComplete
  } = props;
  const [currentStep, setCurrentStep] = useState('CHOOSE_DESTINATION');
  const [selectedTargetHub, setSelectedTargetHub] = useState(null);
  const [isDiffTaskRunning, setIsDiffTaskRunning] = useState(false);
  const {
    portal
  } = userInfoSync();
  const [allowedAssets, disallowedAssets] = useAllowedAssets(assets);
  const handleCopySuccess = useCallback(() => {
    trackInteraction({
      action: 'COPY_SUCCESS',
      count: allowedAssets.length.toString(),
      assetType: portableType
    });
    setCurrentStep('SUCCESS');
    onCopyComplete === null || onCopyComplete === void 0 || onCopyComplete();
  }, [allowedAssets.length, portableType, onCopyComplete]);
  const {
    targetHubs,
    currentAccountName,
    loading: targetHubsLoading
  } = useTargetHubs();
  const onError = () => {
    setCurrentStep('ERROR');
    setIsDiffTaskRunning(false);
  };
  const {
    allPortableKeys,
    portableKeyMap,
    fetchPortableKeys
  } = usePortableKeys({
    variables: {
      assetIds: allowedAssets.map(({
        assetId
      }) => assetId),
      portableType
    },
    assets: allowedAssets,
    skip: true,
    onError
  });
  const {
    completedTask,
    refetch: fetchStatus
  } = useCopyAssetTaskStatus({
    onCompleted: () => {
      setIsDiffTaskRunning(false);
      setCurrentStep('CONFIRM_SELECTION');
    }
  });
  const {
    mutate: createSyncTaskMutate
  } = useCreateSyncTask({
    onError
  });
  const goToNextStep = useCallback(async targetHub => {
    const hubToUse = targetHub || selectedTargetHub;
    if (!hubToUse) {
      return;
    }
    const currentIndex = orderedSteps.indexOf(currentStep);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = currentIndex + 1;
    const nextStep = orderedSteps[nextIndex];
    if (nextIndex < orderedSteps.length) {
      setCurrentStep(nextStep);
    }
    try {
      switch (nextStep) {
        case 'VALIDATION':
          {
            setIsDiffTaskRunning(true);

            // All asset types (including object-lists) need to fetch portable keys
            const portableKeys = await fetchPortableKeys();
            if (portableKeys.length > 0) {
              await createSyncTaskMutate({
                variables: {
                  command: 'DIFF',
                  fromHubId: portal.portal_id,
                  portableKeys,
                  portableType,
                  toHubId: hubToUse.hubId
                },
                onCompleted: data => {
                  const taskId = 'syncTaskV3' in data ? data.syncTaskV3.id : data.syncTask.id;
                  void fetchStatus({
                    taskId
                  });
                }
              });
            } else {
              await createSyncTaskMutate({
                variables: {
                  command: 'DIFF',
                  fromHubId: portal.portal_id,
                  portableKeys,
                  portableType,
                  toHubId: hubToUse.hubId
                },
                onCompleted: data => {
                  const taskId = 'syncTaskV3' in data ? data.syncTaskV3.id : data.syncTask.id;
                  void fetchStatus({
                    taskId
                  });
                }
              });
            }
            return;
          }
        case 'AWAITING_ACTION':
          {
            // All asset types use the same portable keys
            await createSyncTaskMutate({
              variables: {
                command: 'SYNC',
                fromHubId: portal.portal_id,
                portableKeys: allPortableKeys,
                portableType,
                toHubId: hubToUse.hubId
              },
              onCompleted: handleCopySuccess
            });
            return;
          }
        default:
          return;
      }
    } catch (_unused) {
      onError();
    }
  }, [allPortableKeys, createSyncTaskMutate, currentStep, fetchStatus, handleCopySuccess, portableType, portal.portal_id, selectedTargetHub, fetchPortableKeys]);
  useEffect(() => {
    if (!targetHubsLoading && currentStep === 'CHOOSE_DESTINATION' && !selectedTargetHub) {
      const enabledHubs = targetHubs.filter(hub => hub.enabled);
      if (enabledHubs.length === 1) {
        const singleHub = enabledHubs[0];
        setSelectedTargetHub(singleHub);
        void goToNextStep(singleHub);
      }
    }
  }, [targetHubs, targetHubsLoading, currentStep, selectedTargetHub, goToNextStep]);
  const [copyableAssets, skippedAssets] = useMemo(() => {
    const _copyableAssets = [];
    const _skippedAssets = [...disallowedAssets];
    if (isDiffTaskRunning || !completedTask) {
      return [allowedAssets, _skippedAssets];
    }
    allowedAssets.forEach(asset => {
      var _completedTask$diffSu, _completedTask$diffCo;
      const compareKey = portableType === 'object-lists' ? asset.assetName : portableKeyMap[asset.assetId];
      const entriesOnlyOnLeft = (_completedTask$diffSu = completedTask.diffSummary) === null || _completedTask$diffSu === void 0 ? void 0 : _completedTask$diffSu.entriesOnlyOnLeft;
      const componentsOnlyOnSource = (_completedTask$diffCo = completedTask.diffComponentsSummary) === null || _completedTask$diffCo === void 0 ? void 0 : _completedTask$diffCo.componentsOnlyOnSource;
      const diffEntries = entriesOnlyOnLeft || componentsOnlyOnSource || [];
      const canCopy = portableType === 'object-lists' ? diffEntries.find(entry => entry.displayLabel === compareKey) !== undefined : diffEntries.find(entry => entry.portableKey === compareKey) !== undefined;
      if (canCopy) {
        _copyableAssets.push(asset);
      } else {
        _skippedAssets.push(Object.assign({}, asset, {
          skipReason: 'ALREADY_COPIED'
        }));
      }
    });
    return [_copyableAssets, _skippedAssets];
  }, [allowedAssets, disallowedAssets, completedTask, portableKeyMap, isDiffTaskRunning, portableType]);
  return /*#__PURE__*/_jsx(AssetCopierContext.Provider, {
    value: {
      copyableAssets,
      skippedAssets,
      totalAssetsCount: assets.length,
      currentStep,
      setCurrentStep,
      goToNextStep,
      selectedTargetHub,
      setSelectedTargetHub,
      isDiffTaskRunning,
      currentAccountName
    },
    children: children
  });
}
export const useAssetCopierContext = () => {
  const context = useContext(AssetCopierContext);
  if (!context) {
    throw new Error('useAssetCopier must be used within an AssetCopierProvider');
  }
  return context;
};